import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, AlertCircle, Package, ChevronRight, AlertTriangle, Shield } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { regulatoryReadiness, complianceGaps } from '@/data/mockData';
import GradeTooltip from '@/components/GradeTooltip';

const frameworkDetails = {
  'eu-ai': { name: 'EU AI Act', coverage: 68, gaps: 7, deadline: 'Annex III: Aug 2, 2026 · Products: Aug 2, 2027', color: '#F59E0B' },
  'iso-42001': { name: 'ISO/IEC 42001', coverage: 55, gaps: 12, deadline: 'Certification target: Q4 2026', color: '#F59E0B' },
  'nist-rmf': { name: 'NIST AI RMF', coverage: 71, gaps: 5, deadline: 'Self-assessed', color: '#10B981' },
};

const jurisdictionFrameworks = {
  global: ['eu-ai', 'iso-42001', 'nist-rmf', 'soc2-ai'],
  eu: ['eu-ai', 'iso-42001', 'nist-rmf'],
  na: ['soc2-ai', 'nist-rmf', 'us-state', 'sec-ai'],
  ca: ['ca-aida', 'iso-42001', 'ca-pipeda', 'soc2-ai'],
};

const allFrameworks = {
  'eu-ai': { name: 'EU AI Act', coverage: 68, gaps: 7, deadline: 'Annex III: Aug 2, 2026 · Products: Aug 2, 2027', color: '#F59E0B' },
  'iso-42001': { name: 'ISO/IEC 42001', coverage: 55, gaps: 12, deadline: 'Certification target: Q4 2026', color: '#F59E0B' },
  'nist-rmf': { name: 'NIST AI RMF', coverage: 71, gaps: 5, deadline: 'Self-assessed', color: '#10B981' },
  'soc2-ai': { name: 'SOC 2 Type II — AI Controls', coverage: 61, gaps: 8, deadline: 'Annual examination cycle · AI governance evidence increasingly reviewed', color: '#F59E0B' },
  'us-state': { name: 'US State AI Laws', coverage: 44, gaps: 11, deadline: 'Colorado AI Act: Jan 2026', color: '#EF4444' },
  'sec-ai': { name: 'SEC AI Disclosure', coverage: 62, gaps: 6, deadline: 'Ongoing obligation', color: '#F59E0B' },
  'ca-aida': { name: 'Canadian AIDA', coverage: 51, gaps: 9, deadline: 'Proposed legislation · Progressing through Parliament', color: '#F59E0B' },
  'ca-pipeda': { name: 'PIPEDA AI Guidance', coverage: 67, gaps: 7, deadline: 'OPC guidance active', color: '#F59E0B' },
};

const jurisdictionTabs = [
  { id: 'global', label: 'Global' },
  { id: 'eu', label: 'European Union' },
  { id: 'na', label: 'North America' },
  { id: 'ca', label: 'Canada' },
];

function FrameworkRing({ pct, color }) {
  const data = [{ value: pct }, { value: 100 - pct }];
  return (
    <div className="w-20 h-20 relative">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={28} outerRadius={36} startAngle={90} endAngle={-270} dataKey="value" stroke="none">
            <Cell fill={color} />
            <Cell fill="#1E2A45" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-[#F8FAFC]">{pct}%</div>
    </div>
  );
}

function AuditBuilder() {
  const [step, setStep] = useState(1);

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-2 mb-5">
        <Package size={18} className="text-[#6366F1]" />
        <h3 className="text-[#F8FAFC] font-semibold text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Build Audit Package
        </h3>
      </div>

      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
              s <= step ? 'bg-[#6366F1] text-white' : 'bg-[#1E2A45] text-[#475569]'
            }`}>
              {s < step ? <CheckCircle2 size={14} /> : s}
            </div>
            {s < 4 && <div className={`w-8 h-px ${s < step ? 'bg-[#6366F1]' : 'bg-[#1E2A45]'}`} />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-3">
          <div className="text-xs text-[#64748B] mb-2">Select framework</div>
          {['EU AI Act', 'ISO/IEC 42001', 'NIST AI RMF'].map((fw) => (
            <label key={fw} className="flex items-center gap-3 p-3 rounded-lg border border-[#1E2A45] hover:border-[#6366F1]/30 cursor-pointer transition-colors">
              <input type="radio" name="framework" defaultChecked={fw === 'EU AI Act'} className="accent-[#6366F1]" />
              <span className="text-sm text-[#F8FAFC]">{fw}</span>
            </label>
          ))}
        </div>
      )}
      {step === 2 && (
        <div className="space-y-3">
          <div className="text-xs text-[#64748B] mb-2">Select date range</div>
          <div className="flex gap-3">
            <input type="date" className="flex-1 px-3 py-2 bg-[#080D1A] border border-[#1E2A45] rounded-lg text-sm text-[#F8FAFC] focus:outline-none focus:border-[#6366F1]" defaultValue="2026-03-16" />
            <input type="date" className="flex-1 px-3 py-2 bg-[#080D1A] border border-[#1E2A45] rounded-lg text-sm text-[#F8FAFC] focus:outline-none focus:border-[#6366F1]" defaultValue="2026-04-15" />
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-3">
          <div className="text-xs text-[#64748B] mb-2">Select workflows</div>
          {['Contract Classification', 'Legal Document Drafting', 'Academy Recommendations', 'Media Metadata', 'Claims Document Processing'].map((wf) => (
            <label key={wf} className="flex items-center gap-3 p-2.5 rounded-lg border border-[#1E2A45] hover:border-[#6366F1]/30 cursor-pointer transition-colors">
              <input type="checkbox" defaultChecked className="accent-[#6366F1]" />
              <span className="text-sm text-[#F8FAFC]">{wf}</span>
            </label>
          ))}
        </div>
      )}
      {step === 4 && (
        <div className="text-center py-4">
          <div className="text-sm text-[#F8FAFC] mb-2">Package Summary</div>
          <div className="text-xs text-[#94A3B8] space-y-1 mb-4">
            <div>~2,847 Decision Evidence Records</div>
            <div>9 control narratives</div>
            <div>4 gap remediation reports</div>
          </div>
          <button
            data-testid="generate-package-btn"
            className="bg-[#6366F1] hover:bg-[#818cf8] text-white text-sm font-medium px-6 py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]"
          >
            Generate Package
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#1E2A45]">
        <button
          onClick={() => setStep(Math.max(1, step - 1))}
          className={`text-xs text-[#94A3B8] hover:text-[#F8FAFC] transition-colors ${step === 1 ? 'invisible' : ''}`}
        >
          Back
        </button>
        {step < 4 && (
          <button
            data-testid="audit-next-step"
            onClick={() => setStep(step + 1)}
            className="text-xs text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1"
          >
            Next <ChevronRight size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ComplianceDashboard() {
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  const [jurisdiction, setJurisdiction] = useState('global');

  const activeFrameworkKeys = jurisdictionFrameworks[jurisdiction] || jurisdictionFrameworks.global;
  const activeFrameworks = activeFrameworkKeys.map(k => ({ key: k, ...allFrameworks[k] })).filter(Boolean);

  const filteredGaps = frameworkFilter === 'all'
    ? complianceGaps
    : complianceGaps.filter((g) => g.framework === frameworkFilter);

  return (
    <div data-testid="compliance-dashboard" className="space-y-6">
      {/* Translation layer statement */}
      <div className="text-xs text-[#64748B]">Apex maps AI decisions, controls, and incidents to regulatory and assurance frameworks. Coverage reflects evidence already captured — no additional data collection required.</div>

      {/* Jurisdiction selector */}
      <div className="flex items-center justify-center" data-testid="jurisdiction-tabs">
        <div className="inline-flex bg-[#0F1629] border border-[#1E2A45] rounded-full p-1 gap-1">
          {jurisdictionTabs.map((tab) => (
            <span key={tab.id} data-testid={`jurisdiction-${tab.id}`}
              className="px-4 py-1.5 rounded-full text-xs font-medium text-[#64748B]">
              {tab.label}
            </span>
          ))}
        </div>
      </div>

      {/* Control State Coverage Metric */}
      <div className="glass-card p-5" data-testid="css-coverage-metric">
        <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-[0.12em] mb-1">Control State Coverage</div>
        <div className="text-[10px] text-[#64748B] mb-3">Control State at Decision Time · All governed workflows</div>
        <div className="grid grid-cols-4 gap-4">
          <div><div className="text-[10px] text-[#64748B]">April 2026 average</div><div className="text-lg font-bold text-[#10B981]">91.3% ◆</div></div>
          <div><div className="text-[10px] text-[#64748B]">Lowest point</div><div className="text-lg font-bold text-[#F59E0B]">71% ⚠</div><div className="text-[8px] text-[#475569]">April 15, 14:31 UTC</div></div>
          <div><div className="text-[10px] text-[#64748B]">Current</div><div className="text-lg font-bold text-[#10B981]">94% ◆</div></div>
          <div><div className="text-[10px] text-[#64748B]">Frameworks mapped</div><div className="text-[10px] text-[#94A3B8]">EU AI Act · ISO 42001 · NIST AI RMF · SOC 2</div></div>
        </div>
        <Link to="/dashboard/proof-chain" className="text-[9px] text-[#6366F1] hover:text-[#818cf8] mt-2 inline-block">View Control State history →</Link>
      </div>

      {/* Classification Coverage Section — framework-aware */}
      <div className="glass-card p-5" data-testid="classification-coverage">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={16} className="text-[#6366F1]" />
          <h3 className="text-sm font-semibold text-[#F8FAFC]">Classification Coverage</h3>
        </div>
        {(() => {
          const euRows = [
            { icon: 'ok', text: '5/5 systems have current Classification Evidence Records' },
            { icon: 'warn', text: '1 system flagged for potential legal status review (Claude Sonnet 3.7)' },
            { icon: 'warn', text: '1 classification expiring within 60 days (GPT-4o Mini · Due: Jun 15, 2026)' },
            { icon: 'ok', text: '4/5 systems: Deployer role confirmed at decision time' },
            { icon: 'warn', text: '1 system: Review recommended — fine-tuning detected since last classification' },
          ];
          const isoRows = [
            { icon: 'ok', text: '5/5 systems registered in AI System Inventory' },
            { icon: 'ok', text: '3/5 systems have ISO 42001 classification documentation current' },
            { icon: 'warn', text: '2 systems: Documentation refresh due Q3 2026' },
          ];
          const soc2Rows = [
            { icon: 'ok', text: '5/5 in-scope systems have current governance proof records' },
            { icon: 'ok', text: 'Processing Integrity evidence: 94% coverage across in-scope workflows' },
            { icon: 'warn', text: '8 controls with evidence gaps — see gap register below' },
          ];
          const hasEU = activeFrameworkKeys.includes('eu-ai');
          const hasISO = activeFrameworkKeys.includes('iso-42001');
          const hasSOC = activeFrameworkKeys.includes('soc2-ai');
          const rows = hasEU ? euRows : hasISO ? isoRows : hasSOC ? soc2Rows : euRows;
          const label = hasEU ? 'EU AI Act — 5 AI systems registered' : hasISO ? 'ISO/IEC 42001 — 5 AI systems registered' : hasSOC ? 'SOC 2 — AI systems in examination scope' : '';
          return (
            <>
              <div className="text-[9px] text-[#64748B] mb-2">{label}</div>
              <div className="space-y-1.5">
                {rows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    {row.icon === 'ok' ? <CheckCircle2 size={12} className="text-[#10B981] flex-shrink-0" /> : <AlertTriangle size={12} className="text-[#F59E0B] flex-shrink-0" />}
                    <span className="text-[#94A3B8]">{row.text}</span>
                  </div>
                ))}
              </div>
              {hasEU && <div className="text-[9px] text-[#475569] mt-2">Apex flags evidence for attention — legal determination requires qualified counsel.</div>}
              <Link to="/dashboard/ai-inventory" className="text-[10px] text-[#6366F1] hover:text-[#818cf8] flex items-center gap-1 mt-2">
                {hasEU ? 'Review Classification Status' : hasISO ? 'View Registration Status' : 'View SOC 2 AI Evidence'} → <ArrowRight size={10} />
              </Link>
            </>
          );
        })()}
      </div>

      {/* Framework cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {activeFrameworks.map((fw) => (
          <div key={fw.key} className="glass-card p-6" data-testid={`framework-card-${fw.key}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-[#F8FAFC] font-semibold text-base mb-1">{fw.name}</h3>
                <div className="text-xs text-[#64748B] mb-3">{fw.deadline}</div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-[#94A3B8]">{fw.gaps} open gaps</span>
                </div>
                <button
                  data-testid={`view-details-${fw.key}`}
                  onClick={() => setFrameworkFilter(fw.name)}
                  className="text-xs text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1 mt-2"
                >
                  View details <ArrowRight size={12} />
                </button>
              </div>
              <FrameworkRing pct={fw.coverage} color={fw.color} />
            </div>
          </div>
        ))}
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Gap Register */}
        <div className="lg:col-span-2 glass-card p-6" data-testid="gap-register">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[#F8FAFC] font-semibold text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Gap Register
            </h3>
            <div className="flex items-center gap-2">
              {frameworkFilter !== 'all' && (
                <button
                  onClick={() => setFrameworkFilter('all')}
                  className="text-[10px] text-[#64748B] hover:text-[#F8FAFC] transition-colors bg-[#1E2A45] px-2 py-1 rounded"
                >
                  Clear filter
                </button>
              )}
              <span className="text-xs text-[#64748B]">{filteredGaps.length} gaps</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1E2A45]">
                  {['Control ID', 'Framework', 'Description', 'Models', 'Severity', 'Owner', 'Due Date', 'Status'].map((h) => (
                    <th key={h} className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-2 px-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredGaps.map((gap) => (
                  <tr key={gap.controlId} className="border-b border-[#1E2A45]/50" data-testid={`gap-row-${gap.controlId}`}>
                    <td className="py-2.5 px-3 font-mono text-[#6366F1] text-xs">{gap.controlId}</td>
                    <td className="py-2.5 px-3 text-[#94A3B8] text-xs">{gap.framework}</td>
                    <td className="py-2.5 px-3 text-[#94A3B8] text-xs max-w-[200px] truncate">{gap.description}</td>
                    <td className="py-2.5 px-3 text-[#64748B] text-xs">{gap.affectedModels}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                        gap.severity === 'critical' ? 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20' : 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20'
                      }`}>
                        {gap.severity}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      {gap.owner === 'Unassigned' ? (
                        <button data-testid={`assign-owner-${gap.controlId}`} onClick={() => toast.info(`Opening owner assignment for ${gap.controlId}`)} className="text-[10px] text-[#6366F1] hover:text-[#818cf8] border border-[#6366F1]/30 px-2 py-0.5 rounded transition-colors">
                          Assign
                        </button>
                      ) : (
                        <span className="text-xs text-[#94A3B8]">{gap.owner}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-[#64748B] text-xs whitespace-nowrap">{gap.dueDate}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                        gap.status === 'Open' ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'bg-[#3B82F6]/10 text-[#3B82F6]'
                      }`}>
                        {gap.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Audit Package Builder */}
        <div className="lg:col-span-1">
          <AuditBuilder />
        </div>
      </div>
    </div>
  );
}
